/**
 * CreateDocumentsModal.tsx
 *
 * 書類作成フロー（document-create-flow 設計書）:
 * 「何を作りたいか（成果物ベース）」で入口を分ける作成モーダル。
 * menu / invoices / estimate / other の4ステップを持つ。
 * バックエンド API は estimate-invoice-sync 設計書のものをそのまま使う（API 変更なし）。
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  casesApi,
  documentsApi,
  getDocTypeLabel,
  type DocumentSummary,
  type EstimateSyncStatus,
} from "../../api.js";
import { Modal } from "../Modal.js";
import { Button } from "../ui/Button.js";
import { toast } from "../Toast.js";
import type { EstimateApiError } from "../document/EstimateSyncCard.js";
import { PROFESSION_COLOR } from "../../lib/workItemConstants.js";
import { tenantDocumentTypesApi } from "../../api/tenantDocumentTypesApi.js";

type Step = "menu" | "invoices" | "estimate";

/**
 * 「請求書を作成」ステップのチェックボックス1件（tenant_document_types 由来）。
 * 設計書: docs/design/document-type-master-phase2.md §8.3
 * `kind='invoice'` かつ `profession` が設定されている書類種別（＝特定の資格者が発行する
 * 個別の請求書）だけを対象にする。`profession=NULL` の invoice（保証協会請求書）は
 * 従来通り「その他の書類」に残す。
 */
type InvoiceDocTypeOption = { docType: string; label: string; color: string; isBuiltin: boolean };

type Props = {
  caseId: string;
  // 注: 設計書では CaseDocument と表記されているが、既存の型定義は DocumentSummary。
  docs: DocumentSummary[];
  workItemCount: number;
  estimateStatus: EstimateSyncStatus | undefined;
  onClose: () => void;
  /** 実施業務から作成が選ばれたとき。CasePage 側で GenerateDocumentsModal を開く */
  onSelectWorkItems: () => void;
};

export function CreateDocumentsModal({ caseId, docs, workItemCount, estimateStatus, onClose, onSelectWorkItems }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>("menu");

  function invalidateCase() {
    qc.invalidateQueries({ queryKey: ["case", caseId] });
    qc.invalidateQueries({ queryKey: ["case", caseId, "estimate-status"] });
  }

  // ── step "invoices" 用の状態 ────────────────────────────────────────────
  // 組み込み3資格のハードコードチェックボックスを撤去し、tenant_document_types
  // （kind='invoice' かつ profession が設定されている行）から動的に生成する
  // （document-type-master-phase2 設計書 §8.3）。
  const { data: docTypesData, isLoading: docTypesLoading, isError: docTypesErrored } = useQuery({
    queryKey: ["tenant-document-types"],
    queryFn: () => tenantDocumentTypesApi.list(),
  });
  const invoiceDocTypeOptions = useMemo<InvoiceDocTypeOption[]>(() => {
    const rows = docTypesData?.data ?? [];
    return rows
      .filter((d) => d.kind === "invoice" && !!d.profession)
      .sort((a, b) => (b.is_builtin ? 1 : 0) - (a.is_builtin ? 1 : 0) || a.display_order - b.display_order)
      .map((d) => ({
        docType: d.doc_type,
        label: d.label,
        color: PROFESSION_COLOR[d.profession ?? "other"] ?? PROFESSION_COLOR.other!,
        isBuiltin: d.is_builtin,
      }));
  }, [docTypesData]);

  const [selectedDocTypes, setSelectedDocTypes] = useState<string[]>([]);
  // ユーザーが手動で切り替えたら以後はその値を優先する（未操作なら sync 状態から既定値を導出）
  const [withEstimateOverride, setWithEstimateOverride] = useState<boolean | null>(null);

  function toggleDocType(docType: string) {
    setSelectedDocTypes((prev) => (prev.includes(docType) ? prev.filter((x) => x !== docType) : [...prev, docType]));
  }

  const estimateCheckbox = describeEstimateCheckbox(estimateStatus);
  const withEstimate = estimateCheckbox.disabled ? false : (withEstimateOverride ?? estimateCheckbox.defaultChecked);

  const createBundleMut = useMutation({
    mutationFn: async () => {
      const createdDocs: { id: string; doc_type: string }[] = [];
      for (const option of invoiceDocTypeOptions) {
        if (!selectedDocTypes.includes(option.docType)) continue;
        const doc = await documentsApi.create(caseId, {
          doc_type: option.docType,
          line_items: [{ line_order: 1, description: "", fee_amount: 0, tax_amount: 0 }],
        });
        createdDocs.push({ id: doc.id, doc_type: doc.doc_type });
      }

      let estimateMode: "create" | "refresh" | null = null;
      if (withEstimate) {
        const mode: "create" | "refresh" = estimateStatus?.estimate?.status === "draft" ? "refresh" : "create";
        try {
          await casesApi.estimateGenerate(caseId, mode);
          estimateMode = mode;
        } catch (e) {
          // レース: 他タブで draft が作られていた場合は refresh にフォールバック
          if (mode === "create" && (e as EstimateApiError).body?.error?.code === "ESTIMATE_DRAFT_EXISTS") {
            await casesApi.estimateGenerate(caseId, "refresh");
            estimateMode = "refresh";
          } else {
            throw e;
          }
        }
      }
      return { createdDocs, estimateMode };
    },
    onSuccess: ({ createdDocs, estimateMode }) => {
      invalidateCase();
      const invoiceLabels = createdDocs.map(
        (d) => invoiceDocTypeOptions.find((o) => o.docType === d.doc_type)?.label ?? getDocTypeLabel(d.doc_type),
      );
      toast(buildBundleToastMessage(invoiceLabels, estimateMode));
      if (createdDocs.length === 1) {
        // document-create-flow §6-4(b): 見積書を作らず請求書1通だけ作成した場合のみ、
        // 遷移先の DocumentPage で「見積書も作成しますか？」を1度だけ案内する
        const shouldPromptEstimate = estimateMode === null;
        navigate(
          `/documents/${createdDocs[0]!.id}`,
          shouldPromptEstimate ? { state: { promptEstimate: true } } : undefined,
        );
      }
      onClose();
    },
    onError: (e: EstimateApiError) => {
      // 部分失敗: 作成済みの draft はそのまま残す。一覧に反映されるよう invalidate だけ行う
      invalidateCase();
      toast(e.message, "error");
    },
  });

  // ── step "estimate" 用のミューテーション ────────────────────────────────
  const createFromInvoicesMut = useMutation({
    mutationFn: () => casesApi.estimateGenerate(caseId, "create"),
    onSuccess: (doc) => {
      invalidateCase();
      toast("見積書を自動生成しました");
      navigate(`/documents/${doc.id}`);
      onClose();
    },
    onError: (e: EstimateApiError) => {
      invalidateCase();
      if (e.body?.error?.code === "ESTIMATE_DRAFT_EXISTS") {
        toast("既存の下書きがあります。表示を最新の状態に更新しました。", "error");
      } else {
        toast(e.message, "error");
      }
    },
  });

  const createBlankEstimateMut = useMutation({
    mutationFn: () => documentsApi.create(caseId, {
      doc_type: "estimate",
      line_items: [{ line_order: 1, description: "", fee_amount: 0, tax_amount: 0 }],
    }),
    onSuccess: (doc) => {
      invalidateCase();
      toast("見積書を作成しました");
      navigate(`/documents/${doc.id}`);
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });

  // ── step "menu" ────────────────────────────────────────────────────────
  if (step === "menu") {
    return (
      <Modal title="書類を作成" onClose={onClose}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 420 }}>
          <CreateMethodCard
            icon="📋"
            title="実施業務から一括作成"
            recommended
            disabled={workItemCount === 0}
            disabledReason="実施業務がまだ登録されていません"
            description="上の「実施業務」に登録した内容から、司法書士請求書・行政書士請求書・調査士請求書・見積書を一括で下書き作成します（明細は自動転記）。作成後は1件ずつ体裁を整えて発行できます。"
            onClick={onSelectWorkItems}
          />
          <CreateMethodCard
            icon="🧾"
            title="請求書を作成"
            description="資格ごとに請求書を作成します（法律上、資格ごとに請求書を別々に発行します）。見積書も同時に作成できます。"
            onClick={() => setStep("invoices")}
          />
          <CreateMethodCard
            icon="📄"
            title="見積書を作成"
            description="お客様が総額を把握するための見積書を作成します。請求書の合算から自動生成するか、請求書とは独立に白紙から作成できます。"
            onClick={() => setStep("estimate")}
          />
        </div>
      </Modal>
    );
  }

  // ── step "invoices" ────────────────────────────────────────────────────
  if (step === "invoices") {
    return (
      <Modal title="請求書を作成" onClose={onClose}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 420 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 8 }}>
              作成する請求書（業務ごとに1通ずつ作成されます）
            </div>
            {/* reviewer 指摘（推奨）: クエリ失敗時に永久ローディング表示になるのを防ぐため、
                isLoading/isError で分岐する（0件表示のまま固まる問題の修正）。 */}
            {docTypesErrored && (
              <div style={{ fontSize: 12, color: "#dc2626" }}>請求書種別の取得に失敗しました。再読み込みしてください</div>
            )}
            {docTypesLoading && (
              <div style={{ fontSize: 12, color: "#94a3b8" }}>読み込み中...</div>
            )}
            {!docTypesLoading && !docTypesErrored && invoiceDocTypeOptions.length === 0 && (
              <div style={{ fontSize: 12, color: "#94a3b8" }}>作成できる請求書種別がありません</div>
            )}
            {invoiceDocTypeOptions.map((option) => {
              const checked = selectedDocTypes.includes(option.docType);
              const hasDraft = docs.some((d) => d.doc_type === option.docType && d.status === "draft");
              return (
                <label
                  key={option.docType}
                  style={{
                    display: "flex", flexDirection: "column", gap: 2,
                    padding: "8px 10px", borderRadius: 7,
                    border: `1px solid ${checked ? "#99f6e4" : "#e2e8f0"}`,
                    background: checked ? "#f0fdfa" : "#f8fafc",
                    marginBottom: 5, cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleDocType(option.docType)} style={{ cursor: "pointer" }} />
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: option.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{option.label}</span>
                  </div>
                  {hasDraft && (
                    <div style={{ fontSize: 11, color: "#92400e", marginLeft: 24 }}>
                      ※ 下書きが既にあります（作成すると2通目になり、見積書は最新の請求書のみを反映します）
                    </div>
                  )}
                </label>
              );
            })}
          </div>

          <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 12 }}>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, opacity: estimateCheckbox.disabled ? 0.6 : 1, cursor: estimateCheckbox.disabled ? "not-allowed" : "pointer" }}>
              <input
                type="checkbox"
                checked={estimateCheckbox.disabled ? estimateCheckbox.defaultChecked : withEstimate}
                disabled={estimateCheckbox.disabled}
                onChange={() => setWithEstimateOverride(!withEstimate)}
                style={{ marginTop: 2, cursor: estimateCheckbox.disabled ? "not-allowed" : "pointer" }}
              />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{estimateCheckbox.label}</div>
                <div style={{ fontSize: 11, color: estimateCheckbox.warn ? "#c2410c" : "#64748b", marginTop: 2 }}>
                  {estimateCheckbox.note}
                </div>
              </div>
            </label>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <Button variant="ghost" onClick={() => setStep("menu")}>← 戻る</Button>
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="outline" onClick={onClose}>キャンセル</Button>
              <Button
                variant="primary"
                disabled={selectedDocTypes.length === 0 || createBundleMut.isPending}
                onClick={() => createBundleMut.mutate()}
              >
                {createBundleMut.isPending ? "作成中…" : "作成する"}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  // ── step "estimate"（残り。menu/invoices は上で return 済み） ─────────────
  return (
      <Modal title="見積書を作成" onClose={onClose}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 420 }}>
          <p style={{ margin: 0, fontSize: 13, color: "#475569" }}>
            見積書はお客様が総額を把握するための書類です。資格ごとの請求書の合算が見積書になります。
          </p>

          {estimateStatus === undefined ? (
            <div style={{ fontSize: 13, color: "#94a3b8" }}>確認中…</div>
          ) : estimateStatus.estimate?.status === "draft" ? (
            <div style={{ padding: 14, borderRadius: 10, border: "1px solid #e2e8f0", background: "#f8fafc" }}>
              <p style={{ margin: "0 0 10px", fontSize: 13, color: "#475569" }}>見積書の下書きが既にあります。</p>
              <Button variant="primary" onClick={() => { navigate(`/documents/${estimateStatus.estimate!.id}`); onClose(); }}>
                開く
              </Button>
              {estimateStatus.sync === "stale" && (
                <div style={{ fontSize: 11, color: "#c2410c", marginTop: 8 }}>
                  ⚠ 見積書が請求書の内容と一致していません。請求書を保存すると自動で合算内容に更新されます
                </div>
              )}
            </div>
          ) : estimateStatus.sync === "issued" ? (
            <div style={{ padding: 14, borderRadius: 10, border: "1px solid #e2e8f0", background: "#f8fafc" }}>
              <p style={{ margin: "0 0 10px", fontSize: 13, color: "#475569" }}>
                見積書 v{estimateStatus.estimate?.version} は発行済みです。内容を変えるには見積書画面から修正版を作成してください。
              </p>
              <Button variant="outline" onClick={() => { navigate(`/documents/${estimateStatus.estimate!.id}`); onClose(); }}>
                開く
              </Button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <CreateMethodCard
                icon="🧮"
                title="請求書の合算から生成"
                description="この案件の資格ごとの請求書の明細を合算して見積書を作ります。請求書を先に作った場合はこちらが確実です。"
                disabled={estimateStatus.sync === "no_invoices"}
                disabledReason="集約できる請求書がありません。先に請求書を作るか、下の「白紙から作成」を使ってください"
                loading={createFromInvoicesMut.isPending}
                onClick={() => createFromInvoicesMut.mutate()}
              />
              <CreateMethodCard
                icon="✏️"
                title="白紙から作成（請求書と独立）"
                description="請求書がなくても、明細を手入力して見積書だけを先に作れます。後から請求書を作成・保存すると、自動で請求書の合算内容に更新されます。"
                loading={createBlankEstimateMut.isPending}
                onClick={() => createBlankEstimateMut.mutate()}
              />
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-start", gap: 8 }}>
            <Button variant="ghost" onClick={() => setStep("menu")}>← 戻る</Button>
            <Button variant="outline" onClick={onClose}>キャンセル</Button>
          </div>
        </div>
      </Modal>
  );
}

// ── ヘルパー ──────────────────────────────────────────────────────────────

function buildBundleToastMessage(invoiceLabels: string[], estimateMode: "create" | "refresh" | null): string {
  if (estimateMode === "refresh") {
    return `${invoiceLabels.join("・")}を作成し、見積書を更新しました`;
  }
  if (estimateMode === "create") {
    return `${[...invoiceLabels, "見積書"].join("・")}を作成しました`;
  }
  return `${invoiceLabels.join("・")}を作成しました`;
}

function describeEstimateCheckbox(status: EstimateSyncStatus | undefined): {
  label: string;
  note: string;
  disabled: boolean;
  warn: boolean;
  defaultChecked: boolean;
} {
  if (status === undefined) {
    return { label: "見積書も同時に作成する", note: "確認中…", disabled: true, warn: false, defaultChecked: false };
  }
  if (status.sync === "issued") {
    return {
      label: "見積書も同時に作成する",
      note: "見積書は発行済みです。内容の変更は見積書画面から修正版を作成してください",
      disabled: true,
      warn: false,
      defaultChecked: false,
    };
  }
  if (status.sync === "stale" || status.sync === "in_sync") {
    // 見積書 draft が既に存在する場合、請求書の作成・保存時にサーバー側で自動同期される
    // （手動の「更新」操作は廃止）。チェックボックスとしての選択は不要。
    return {
      label: "見積書（下書き）は自動で更新されます",
      note: "⚠ 請求書を作成すると見積書の下書きは合算内容に自動更新されます（見積書に手動で加えた明細の変更は上書きされます）",
      disabled: true,
      warn: true,
      defaultChecked: true,
    };
  }
  // not_created / no_invoices
  return {
    label: "見積書も同時に作成する",
    note: "お客様向けの総額書類。作成した請求書の合算から自動生成されます",
    disabled: false,
    warn: false,
    defaultChecked: true,
  };
}

// ── 作成方法カード（CasePage から移設） ─────────────────────────────────────
function CreateMethodCard({
  icon, title, description, recommended, disabled, disabledReason, loading, onClick,
}: {
  icon:           string;
  title:          string;
  description:    string;
  recommended?:   boolean;
  disabled?:      boolean;
  disabledReason?: string;
  loading?:       boolean;
  onClick:        () => void;
}) {
  return (
    <button
      type="button"
      onClick={disabled || loading ? undefined : onClick}
      disabled={disabled || loading}
      style={{
        textAlign: "left",
        display: "flex",
        gap: 12,
        padding: 14,
        borderRadius: 10,
        border: `1px solid ${recommended ? "#99f6e4" : "#e2e8f0"}`,
        background: disabled ? "#f8fafc" : recommended ? "#f0fdfa" : "#fff",
        cursor: disabled || loading ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        fontFamily: "inherit",
      }}
    >
      <div style={{ fontSize: 22, flexShrink: 0 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{title}</span>
          {recommended && (
            <span style={{ fontSize: 10, fontWeight: 700, color: "#0f766e", background: "#ccfbf1", padding: "2px 6px", borderRadius: 4 }}>
              推奨
            </span>
          )}
          {loading && (
            <span style={{ fontSize: 11, color: "#64748b" }}>生成中…</span>
          )}
        </div>
        <div style={{ fontSize: 12, color: "#475569", marginTop: 4, lineHeight: 1.5 }}>
          {description}
        </div>
        {disabled && disabledReason && (
          <div style={{ fontSize: 11, color: "#dc2626", marginTop: 6 }}>
            ※ {disabledReason}
          </div>
        )}
      </div>
    </button>
  );
}
